import React from 'react';

import {
  useAddVideoCommentMutation,
  User,
  useVideoCommentsQuery,
  useViewerQuery,
  VideoComment,
  VideoCommentsDocument,
} from '@edx/graphql/generated';
import { useValidationErrors } from '@edx/hooks/use-validation-errors';
import { AuthModule } from '@edx/components/auth-module';
import { Avatar } from '@edx/components/avatar';

import { AddCommentForm, CommentItem } from './components';
import { Button } from '@edx/components/button';

type Props = {
  courseSlug: string;
  videoSlug: string;
};

const getFullName = ({ firstName, lastName }: { firstName: string; lastName: string }): string =>
  `${firstName} ${lastName}`;

export const VideoComments: React.FC<Props> = ({ courseSlug, videoSlug }) => {
  const { data } = useVideoCommentsQuery({ variables: { courseSlug, slug: videoSlug } });
  const [onAddComment, { error }] = useAddVideoCommentMutation();
  const { errors: validationErrors } = useValidationErrors(error?.graphQLErrors);
  const [showReplyToCommentId, setShowReplyToCommentId] = React.useState<string>();

  const viewerQuery = useViewerQuery({ ssr: false });
  const viewer = viewerQuery.data?.viewer;
  const showAuthModule = !viewerQuery.loading && !viewer;

  const comments = data?.video?.comments || [];

  const viewerCanComment = viewer && data?.video?.viewerCanPlay;

  const handleAddVideoComment = ({ body, commentId }: { body: string; commentId?: string }): void => {
    onAddComment({
      variables: { input: { videoId: data?.video?.id, body, commentId } },
      refetchQueries: [{ query: VideoCommentsDocument, variables: { courseSlug, slug: videoSlug } }],
    });
  };

  const title =
    comments.length === 0
      ? 'Comentarios'
      : `${comments.length} ${comments.length === 1 ? 'Comentario' : 'Comentarios'}`;

  return (
    <section className="bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-10 pt-6 pb-16">
        {(viewer || comments.length > 0) && <h3 className="uppercase tracking-widest">{title}</h3>}

        {viewerCanComment && (
          <div className="max-w-2xl mt-6">
            <AddCommentForm
              author={viewer}
              buttonText="Comentar"
              onSubmit={handleAddVideoComment}
              validationErrors={validationErrors}
            />
          </div>
        )}

        {comments.length > 0 && (
          <div className="max-w-2xl mt-6 flex flex-col space-y-4">
            {comments.map(({ id, author, bodyHTML, createdAt, replies }) => (
              <div key={`comment-${id}`} className="flex space-x-2" data-testid="comment">
                <Avatar name={getFullName(author)} src={author.avatarUrl} />

                <div className="w-full flex flex-col">
                  <CommentItem author={author} bodyHTML={bodyHTML} createdAt={createdAt} />

                  {Array.isArray(replies) && (
                    <ul className="mt-3 space-y-4">
                      {(replies as VideoComment[]).map((reply) => (
                        <li key={`comment-${id}-reply-${reply.id}`} className="flex space-x-2">
                          <Avatar name={getFullName(reply.author)} src={author.avatarUrl} />

                          <CommentItem author={reply.author} bodyHTML={reply.bodyHTML} createdAt={reply.createdAt} />
                        </li>
                      ))}
                    </ul>
                  )}

                  {viewerCanComment &&
                    (showReplyToCommentId === id ? (
                      <div className="my-2">
                        <AddCommentForm
                          author={viewer as User}
                          buttonText="Responder"
                          onSubmit={({ body }): void => handleAddVideoComment({ commentId: id, body })}
                          validationErrors={validationErrors}
                          showAvatar={false}
                        />
                      </div>
                    ) : (
                      <div className="mt-1">
                        <Button
                          variant="silentGray"
                          className="text-gray-500"
                          size="xsmall"
                          textVariant="medium"
                          onClick={(): void => setShowReplyToCommentId(id)}
                        >
                          Responder
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {showAuthModule && (
          <div className="mt-8">
            <div className="max-w-3xl">
              <AuthModule />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
